import { NativeModules } from 'react-native';
import Toast from 'react-native-toast-message';
import type { ReactNativeBridge } from '../../../native-module';
import { isWeb } from '../isWeb';
import type { Simplify } from 'type-fest';
import type { ILogging } from '../../api/types';
import { logAdEvent as logAdEventFn } from './logAdEvent/logAdEvent';
import { logAnalyticsEvent as logAnalyticsEventFn } from './logAnalyticsEvent/logAnalyticsEvent';
import {
  likeGoods as likeGoodsFn,
  unlikeGoodsInFolder as unlikeGoodsInFolderFn,
  getLocalGoodsLike as getLocalGoodsLikeFn,
} from './goodsLikeSync/goodsLikeSync';
import type {
  LikeGoodsParams,
  UnlikeGoodsInFolderParams,
} from './goodsLikeSync/types';
import { removeMemberToken } from '../removeMemberToken';
import { resolveWebURL } from '../resolveWebURL';
import { sanitizeRedirectPath } from '../sanitizeRedirectPath';
import { convertDeeplinkToWebLink } from '../convertDeeplinkToWebLink/convertDeeplinkToWebLink';
import type { LogAnalyticsEventParams } from './logAnalyticsEvent/types';
import type { LogAdEventParams } from './logAdEvent/types';
import { mergeLogging } from './logAnalyticsEvent/mergeLogging';
import { normalizeAnalyticsBooleans } from './logAnalyticsEvent/normalizeAnalyticsBooleans';
import DeviceInfo from '../DeviceInfo';
import { compareVersions } from 'compare-versions';
import { isAndroid } from '../isAndroid';

type PathString = `/${string}`;

class Bridge {
  /** 앱 패키지명 (Android applicationId / iOS bundleIdentifier). 네이티브 동기 상수 */
  get packageName(): string {
    if (isWeb) return '';
    return NativeModules.ReactNativeBridge?.packageName ?? '';
  }

  toast(message: string) {
    if (isWeb) {
      Toast.show({
        type: 'success',
        text1: message,
        position: 'bottom',
      });
      return;
    }
    NativeModules.ReactNativeBridge.toast(message);
  }

  vibrate(style: string) {
    if (isWeb) return;
    if (isAndroid) return;

    NativeModules.ReactNativeBridge.vibrate(style);
  }

  assertLogin(
    params: Simplify<Parameters<ReactNativeBridge['assertLogin']>[0]> & {
      /** 웹 전용: 로그인 후 복귀할 내부 경로. 미지정 시 현재 경로 */
      redirect?: string;
    },
  ) {
    const { redirect, ...nativeParams } = params;
    if (isWeb) {
      const { pathname, search } = location;
      const target = sanitizeRedirectPath(redirect ?? `${pathname}${search}`);
      resolveWebURL(`/login?redirect=${encodeURIComponent(target)}`).then(
        url => {
          location.href = url;
        },
      );
      return;
    }
    NativeModules.ReactNativeBridge.assertLogin(nativeParams);
  }

  logout(): Promise<void> {
    if (isWeb) {
      // RN-web 로그인은 setMemberToken 으로 member 쿠키를 클라이언트에서 직접 쓰므로,
      // 같은 member 쿠키를 클라이언트에서 제거한 뒤 앱 홈으로 이동해 로그인 상태를 푼다.
      // (레거시 `${baseURL}/logout` 은 존재하지 않는 경로라 404 이므로 쓰지 않는다.)
      return removeMemberToken().then(() => {
        location.href = '/';
      });
    }
    return NativeModules.ReactNativeBridge.logout();
  }

  openDatePicker(
    params: Parameters<ReactNativeBridge['openDatePicker']>[0],
  ): Promise<string | null> {
    if (isWeb) return Promise.resolve(null);
    return NativeModules.ReactNativeBridge.openDatePicker(params);
  }
  /**
   * - 찜 싱크
   * - 플랫폼별 구현은 `goodsLikeSync`(native) / `goodsLikeSync.web`(web 인메모리 캐시)
   */
  likeGoods(goodsParams: LikeGoodsParams) {
    likeGoodsFn(goodsParams);
  }

  /**
   * - 폴더 내 모든 상품 찜 해제
   */
  unlikeGoodsInFolder(params: UnlikeGoodsInFolderParams) {
    unlikeGoodsInFolderFn(params);
  }

  /**
   * 마켓 좋아요 싱크
   * 웹의 경우 싱크하지 않아도 됩니다.
   * @param marketParams
   */
  favoriteMarket(
    marketParams: Simplify<Parameters<ReactNativeBridge['favoriteMarket']>[0]>,
  ) {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.favoriteMarket(marketParams);
    } catch {
      /* empty */
    }
  }

  /**
   * 아웃핏 북마크 싱크
   * 웹의 경우 싱크하지 않습니다.
   */
  bookmarkOutfit(
    params: Simplify<Parameters<ReactNativeBridge['bookmarkOutfit']>[0]>,
  ) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.bookmarkOutfit(params);
  }

  /**
   * 아웃핏 북마크에 대한 로컬 저장소 값을 가져옵니다.
   */
  getLocalOutfitBookmark() {
    if (isWeb) {
      return null;
    }
    try {
      return NativeModules.ReactNativeBridge.getLocalOutfitBookmark({});
    } catch {
      return null;
    }
  }

  async openDeeplink(
    link: string,
    {
      webCallback,
    }: {
      /**
       * - isWeb일때 기본동작이 아닌 webCallback이 실행됩니다.
       */
      webCallback: (...args: unknown[]) => unknown;
    } = {
      webCallback: async () => {
        const webLink = await convertDeeplinkToWebLink(link);
        if (webLink == null) {
          location.href = '/';
          return;
        }
        location.href = webLink;
      },
    },
  ) {
    if (isWeb) {
      webCallback();
      return;
    }
    NativeModules.ReactNativeBridge.openDeeplink(link);
  }

  async openPath(
    path: PathString,
    {
      webPath,
    }: {
      webPath: PathString;
    } = {
      webPath: path,
    },
  ) {
    await this.openDeeplink(`ably:/${path}`, {
      webCallback: async () => {
        // known RN 라우트면 접속 origin을 유지(same-origin), 그 외만 레거시 웹으로.
        location.href = await resolveWebURL(webPath);
      },
    });
  }

  openGoodsShare(params: {
    goods_sno: number;
    affiliate_reward: {
      expected_reward: number;
      share_nudging?: {
        title: string;
        deeplink: string;
      } | null;
      logging?: Record<string, string | number | boolean | null> | null;
    };
    referral_reward?: {
      amount: number;
      deeplink: string;
    };
    is_by_screenshot?: boolean;
    logging_analytics?: Record<string, string | number | boolean | null>;
  }) {
    if (isWeb) {
      const url = `${location.origin}/goods/${params.goods_sno}`;
      // if (navigator.share) {
      //   navigator.share({ url }).catch(() => {});
      // } else {
      navigator.clipboard?.writeText(url).catch(() => {});
      // }
      return;
    }
    NativeModules.ReactNativeBridge.openGoodsShare(params);
  }

  /**
   * - react-native 인스턴스 종료하기
   * @param params
   */
  closeInstance(params: { instanceId?: string; animated?: boolean }) {
    if (isWeb || !params.instanceId) {
      return;
    }

    NativeModules.ReactNativeBridge.closeInstance({
      instanceId: params.instanceId,
      animated: params.animated,
    });
  }

  logAnalyticsEvent({
    logging,
    analytics,
    ably_only_log,
    ...rest
  }: LogAnalyticsEventParams): void | Promise<void> {
    const merged = mergeLogging({ logging, analytics, ably_only_log });
    // 서버 logging 을 spread 로 얹는 호출부가 많아 boolean 이 그대로 올라간다.
    // 규약(`'1'`/`'0'`)에 맞춰 전송 직전에 한 번 정규화한다.
    const params = {
      ...rest,
      ...merged,
      analytics: normalizeAnalyticsBooleans(merged.analytics),
    };
    if (__DEV__) {
      console.log('📕 logAnalyticsEvent', params);
    }
    return logAnalyticsEventFn(params);
  }

  logAdEvent(params: LogAdEventParams) {
    if (__DEV__) {
      console.log('💸 logAdEvent', params);
    }
    logAdEventFn(params);
  }
  /**
   * 상품 좋아요에 대한 로컬 저장소 값을 가져옵니다
   * - web 은 이번 페이지 세션에 사용자가 찜/찜해제한 값(인메모리 캐시)을 돌려줍니다.
   */
  getLocalGoodsLike() {
    return getLocalGoodsLikeFn();
  }

  /**
   * 마켓 좋아요에 대한 로컬 저장소 값을 가져옵니다.
   */
  getLocalMarketFavorite() {
    if (isWeb) {
      return null;
    }
    try {
      return NativeModules.ReactNativeBridge.getLocalMarketFavorite({});
    } catch {
      return null;
    }
  }

  setScreenName(params: { instanceId?: string; name: string }) {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.setScreenName({
        instanceId: params.instanceId,
        name: params.name,
      });
    } catch {
      /* empty*/
    }
  }

  reportTTFD(params: Parameters<ReactNativeBridge['reportTTFD']>[0]): void {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.reportTTFD(params);
    } catch {
      /* empty */
    }
  }

  async updateMemberMe(
    params: Parameters<ReactNativeBridge['updateMemberMe']>[0],
  ): Promise<void> {
    if (isWeb) {
      return;
    }
    const currentVersion = await DeviceInfo.getVersion();
    if (!currentVersion) return;
    const isSupportedVersion = compareVersions(currentVersion, '2.334.0') >= 0;
    if (!isSupportedVersion) return;
    NativeModules.ReactNativeBridge.updateMemberMe(params);
  }

  getFeatureFlag(
    params: Parameters<ReactNativeBridge['getFeatureFlag']>[0],
  ): Promise<string | null> {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.getFeatureFlag(params);
    } catch {
      return Promise.resolve(null);
    }
  }

  async openShareSheet(message: string) {
    if (isWeb) {
      try {
        await navigator.clipboard.writeText(message);
      } catch {
        /* clipboard API not available */
      }
      return;
    }
    NativeModules.ReactNativeBridge.openShareSheet(message);
  }

  openImageShareSheet(url: string) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.openImageShareSheet(url);
  }

  openInstagramStoryShare(url: string) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.openInstagramStoryShare(url);
  }

  async openAblyShareSheet(params: {
    data: {
      type: 'FOLDER';
      url: string;
      folder_title?: string;
      instagram_story_image_url?: string;
      user_profile_image_url?: string;
      user_name?: string;
    };
    logging?: ILogging['analytics'];
  }) {
    if (isWeb) {
      try {
        await navigator.clipboard.writeText(params.data.url);
        Toast.show({
          type: 'success',
          text1: '링크가 클립보드에 복사되었어요',
          position: 'bottom',
        });
      } catch {
        /* clipboard API not available */
      }
      return;
    }
    NativeModules.ReactNativeBridge.openAblyShareSheet(params);
  }

  getGoodsMeasureInfo(params: {
    goods_sno: number;
    full_length: number | null;
  }) {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.getGoodsMeasureInfo(params);
    } catch {
      return Promise.resolve(null);
    }
  }

  openGoodsMeasureInfoInputBottomSheet(params: {
    goods_sno: number;
    full_length: number | null;
  }) {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.openGoodsMeasureInfoInputBottomSheet(
        params,
      );
    } catch {
      return Promise.resolve(null);
    }
  }

  /**
   * 화면 결과 전달 — RN→native(WebView 등) 콜백 또는 RN→RN 라우팅 (ENABLE-579).
   * - `targetInstanceId === instanceId` (자기 자신) → native owner의 onResult 콜백 발화 (WebView 결제 callback case)
   * - 그 외 → `screenResult` 이벤트 broadcast → 부모 RN root의 listener가 수신
   */
  deliverScreenResult(
    params: Parameters<ReactNativeBridge['deliverScreenResult']>[0],
  ): void {
    if (isWeb || !params.instanceId) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.deliverScreenResult(params);
    } catch {
      /* empty */
    }
  }
}

export const bridge = new Bridge();
